import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

import { useMutation } from '@tanstack/react-query';
import {
	deleteTrackedSecurities,
	updateTrackedSecurities,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { SecurityFeatureBaseProps } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';

import { BulkUpdateTrackedSecuritiesDto, IncomeType } from '@dealstack/shared/lib/generated';

import {
	TRACKING_FORM_FIELD,
	useIncomeRightsTrackingController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRightsTracking/useIncomeRightsTrackingController.ts';
import { FormProvider } from 'react-hook-form';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { MultiSelect } from 'react-hook-form-mantine';

import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { SecurityTrackingFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRightsTracking/incomeRightsTracking.zod.ts';

export const IncomeRightsTracking = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
}: PropsWithChildren<SecurityFeatureBaseProps>) => {
	const tCapManage = useTranslationCapManage();
	const [isEdit, setIsEdit] = useState(security?.incomeType !== IncomeType.TRACKING);

	const { form, dealSecurities, resetForm } = useIncomeRightsTrackingController(security);

	const updateTrackedSecuritiesRequest = useMutation({
		mutationFn: updateTrackedSecurities,
	});
	const deleteTrackedSecuritiesRequest = useMutation({
		mutationFn: deleteTrackedSecurities,
	});

	const isLoading =
		updateTrackedSecuritiesRequest.isPending || deleteTrackedSecuritiesRequest.isPending;

	const trackedSecuritiesControl = form.watch(TRACKING_FORM_FIELD.trackedSecurities);

	const trackedSecuritiesData = useMemo(() => {
		return (
			dealSecurities
				?.filter((dealSecurity) => trackedSecuritiesControl?.includes(dealSecurity.value))
				?.map((dealSecurity) => dealSecurity.label)
				?.join(', ') || LONG_DASH
		);
	}, [trackedSecuritiesControl, dealSecurities]);

	const handleUpdateTrackedSecurities = (args: SecurityTrackingFormDto) => {
		if (!security) {
			return;
		}

		const body: BulkUpdateTrackedSecuritiesDto = {
			trackingSecurityId: security.id,
			trackedSecurityIds: args?.trackedSecurities || [],
		};
		updateTrackedSecuritiesRequest.mutate(
			{
				body,
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage(
							'toasts.success.income_rights.tracked_securities_updated_successfully'
						),
					});

					refreshDetails?.(security?.id);
					setIsEdit(false);
				},
			}
		);
	};

	const handleDeleteTrackedSecurities = () => {
		const securityId = security?.id ?? '';
		const trackingSecurityIssuerId = security?.issuerId ?? '';

		deleteTrackedSecuritiesRequest.mutate(
			{ securityId, trackingSecurityIssuerId },
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.tracked_securities_deleted_successfully'),
					});
					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};

	const handleCancel = useCallback(() => {
		resetForm();
		onCancel?.();
	}, [onCancel, resetForm]);

	return (
		<EditableInfoContainer
			title={tCapManage('securities.incomeRights_tracking.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={form.handleSubmit(handleUpdateTrackedSecurities)}
			onRemove={handleDeleteTrackedSecurities}
			onCancel={handleCancel}
			hideRemove={!security?.tracking?.length && security?.incomeType !== IncomeType.TRACKING}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<FormProvider {...form}>
				{isLoading && <InlineLoader />}

				<form
					onSubmit={form.handleSubmit(handleUpdateTrackedSecurities)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={4}>
						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.tracking_form.tracked_securities.label')}
							formInput={
								<MultiSelect
									name={TRACKING_FORM_FIELD.trackedSecurities}
									control={form.control}
									data={dealSecurities}
									searchable
									clearable
									placeholder={tCapManage(
										'securities.tracking_form.tracked_securities.placeholder'
									)}
								/>
							}
							data={trackedSecuritiesData}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
